import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FiltersButton, Button, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, FilterBottomSheet, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { AudienceCard } from '../components/AudienceCard';
import { AudienceListItem } from '../components/AudienceListItem';
import { CreateAudienceModal } from '../components/CreateAudienceModal';
import { audienceStatusMeta } from '../components/audiencePresentation';
import { useAudiences, useDeleteAudience } from '../hooks';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import type { AudienceQuery, AudienceIdentifierType, AudienceResponse } from '@troia-v3/types';

type SortKey = 'name' | 'entries' | 'status' | 'createdAt';

const TYPE_LABEL: Record<AudienceIdentifierType, string> = { phone: 'Telefone', email: 'Email' };

export const AudiencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState<AudienceQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const confirm = useConfirm();

  // "+" central do bottom-nav mobile → novo público nesta tela (mockup `.m-fab`).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); setShowCreate(true); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  // Busca com debounce (preserva comportamento anterior de 400ms).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setQuery((prev) => ({ ...prev, page: 1, search: debouncedSearch || undefined }));
  }, [debouncedSearch]);

  const { data, isLoading, error } = useAudiences(query);
  const deleteAudience = useDeleteAudience();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para públicos).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'entries') cmp = a.totalEntries - b.totalEntries;
      else if (sort.key === 'status') cmp = audienceStatusMeta[a.processingStatus].rank - audienceStatusMeta[b.processingStatus].rank;
      else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((a) => selectedIds.has(a.id));
  const someOnPageSelected = items.some((a) => selectedIds.has(a.id));

  const setTypeFilter = (identifierType?: AudienceIdentifierType) => {
    setQuery((prev) => ({ ...prev, identifierType, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, identifierType: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((a) => next.delete(a.id));
      else items.forEach((a) => next.add(a.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleView = (id: string) => navigate(`/prospecting/audiences/${id}`);

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Tem certeza que deseja excluir este público?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteAudience.mutate(id, {
      onSuccess: () => {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        toast.success('Público excluído');
      },
      onError: (err) => toast.error(err.message || 'Erro ao excluir'),
    });
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} público(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteAudience.mutate(id));
    clearSelection();
  };

  const typeLabel = query.identifierType ? TYPE_LABEL[query.identifierType] : null;
  const activeFilterCount = typeLabel ? 1 : 0;

  const SortHead: React.FC<{ label: string; k: SortKey; className?: string }> = ({ label, k, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid="audiences-select-all"
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Tipo</p>
      {([[undefined, 'Todos os tipos'], ['phone', 'Telefone'], ['email', 'Email']] as const).map(([val, label]) => {
        const active = query.identifierType === val;
        return (
          <button
            key={label}
            onClick={() => setTypeFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`audiences-filter-type-${val ?? 'all'}`}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Prospecção
            </span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Públicos</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar público…"
            data-testid="audiences-search"
          />
        }
        right={
          <>
            {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="audiences-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="audiences-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo público (único primário — F01-03: Button shared 38px, colapso @container) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => setShowCreate(true)}
            data-testid="new-audience"
          >
            Novo público
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <div
              className="flex items-center gap-2 h-[38px] px-3 rounded-[10px] flex-1 min-w-0"
              style={{ backgroundColor: 'var(--theme-surface-sunken, var(--theme-page-background))', border: '1px solid var(--theme-input-border)' }}
            >
              <Icon name="search" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar público…"
                className="flex-1 min-w-0 bg-transparent outline-none text-[13px]"
                style={{ color: 'var(--theme-input-text)' }}
                data-testid="audiences-search-mobile"
              />
            </div>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="audiences-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {typeLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Tipo: {typeLabel}
              <button onClick={() => setTypeFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de tipo" data-testid="audiences-chip-type-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="audiences-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="audiences-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="audiences-bulk-clear">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar públicos: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando públicos..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum público encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie seu primeiro público para começar a segmentar suas campanhas'}
              actionLabel="Novo público"
              onAction={() => setShowCreate(true)}
            />
          )}

          {/* Grid (Cards) — F34: cards sem checkbox, seleção só na view de lista */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sortedItems.map((audience: AudienceResponse) => (
                <AudienceCard
                  key={audience.id}
                  audience={audience}
                  onView={handleView}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((audience: AudienceResponse) => (
                  <AudienceCard
                    key={audience.id}
                    audience={audience}
                    onView={handleView}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos')}
                      </th>
                      <SortHead label="Nome" k="name" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Tipo</th>
                      <SortHead label="Entradas" k="entries" />
                      <SortHead label="Status" k="status" />
                      <SortHead label="Criado em" k="createdAt" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((audience: AudienceResponse) => (
                      <AudienceListItem
                        key={audience.id}
                        audience={audience}
                        selected={selectedIds.has(audience.id)}
                        onToggleSelect={toggleSelect}
                        onView={handleView}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação F48: totalItems/pageSize/itemLabel — "Mostrando X–Y de N públicos" */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (data.total ?? 0) > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={data.total}
              pageSize={query.limit ?? 12}
              itemLabel="públicos"
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="audiences-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      <CreateAudienceModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
};
